import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { ROLE_LABELS } from '@/types/role'
import type { Role } from '@/types/role'
import { ROLE_DASHBOARD } from '@/utils/talkWelt'

function getRoleBadgeClass(role: Role | undefined): string {
  const base = 'px-1.5 py-0.5 rounded text-xs'
  if (!role) return `${base} bg-slate-600 text-white`
  switch (role) {
    case 'ultraadmin': return `${base} bg-red-600 text-white`
    case 'superadmin': return `${base} bg-purple-600 text-white`
    case 'admin': return `${base} bg-blue-600 text-white`
    case 'hr': return `${base} bg-yellow-500 text-slate-900`
    case 'engineer': return `${base} bg-orange-500 text-white`
    case 'partner': return `${base} bg-teal-600 text-white`
    case 'teacher': return `${base} bg-slate-500 text-white`
    default: return `${base} bg-slate-600 text-white`
  }
}

function getRoleDotColor(role: Role): string {
  switch (role) {
    case 'ultraadmin': return 'bg-red-500'
    case 'superadmin': return 'bg-purple-500'
    case 'admin': return 'bg-blue-500'
    case 'hr': return 'bg-yellow-400'
    case 'engineer': return 'bg-orange-500'
    case 'partner': return 'bg-teal-500'
    default: return 'bg-slate-400'
  }
}

export function RoleSelector() {
  const user = useAuthStore((state) => state.user)
  const availableRoles = useAuthStore((state) => state.availableRoles)
  const isFetchingRoles = useAuthStore((state) => state.isFetchingRoles)
  const rolesFetchError = useAuthStore((state) => state.rolesFetchError)
  const switchRole = useAuthStore((state) => state.switchRole)
  const fetchAvailableRoles = useAuthStore((state) => state.fetchAvailableRoles)
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSwitching, setIsSwitching] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const currentRole: Role | undefined = user?.role
  const rolesList = availableRoles ?? []
  const hasMultipleRoles = rolesList.length > 1
  const isLoading = isFetchingRoles && rolesList.length === 0
  const roleLabel = currentRole ? (ROLE_LABELS[currentRole] ?? currentRole) : 'Unbekannt'
  const badgeClass = getRoleBadgeClass(currentRole)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!error) return
    const timer = setTimeout(() => setError(null), 3000)
    return () => clearTimeout(timer)
  }, [error])

  useEffect(() => {
    fetchAvailableRoles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSwitch = async (role: Role) => {
    if (isSwitching || isFetchingRoles) return
    const roleExists = rolesList.some(r => r.role === role)
    if (!roleExists) {
      setError('Rolle nicht verfügbar')
      setIsOpen(false)
      return
    }
    if (role === currentRole) {
      setIsOpen(false)
      return
    }
    setError(null)
    setIsSwitching(true)
    try {
      await switchRole(role)
      await fetchAvailableRoles()
      setIsOpen(false)
      const switched = useAuthStore.getState().user
      if (role === 'engineer' && switched?.needs_school_selection) {
        navigate('/engineer/schools', { replace: true })
        return
      }
      const targetDashboard = ROLE_DASHBOARD[role] || '/dashboard'
      navigate(targetDashboard, { replace: true })
    } catch {
      setError('Rollenwechsel fehlgeschlagen')
      setIsOpen(false)
    } finally {
      setIsSwitching(false)
    }
  }

  if (!user) return null

  if (isLoading) {
    return (
      <div className="text-sm font-medium text-white">
        <span className={`${badgeClass} opacity-50`}>...</span>
      </div>
    )
  }

  if (rolesFetchError && rolesList.length === 0) {
    return (
      <button
        onClick={() => fetchAvailableRoles()}
        className="flex items-center gap-1 text-sm font-medium text-white hover:text-slate-200 transition cursor-pointer"
        title={rolesFetchError}
      >
        <span className={badgeClass}>{roleLabel}</span>
        <span className="w-2 h-2 rounded-full bg-red-500" />
      </button>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => !isSwitching && hasMultipleRoles && setIsOpen(!isOpen)}
        disabled={isSwitching || !hasMultipleRoles}
        className={`flex items-center gap-1 text-sm font-medium text-white transition ${
          hasMultipleRoles
            ? 'hover:text-slate-200 cursor-pointer'
            : 'cursor-default opacity-70'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        title={!hasMultipleRoles ? 'Keine weiteren Rollen verfügbar' : undefined}
      >
        <span className={badgeClass}>
          {isSwitching ? '...' : roleLabel}
        </span>
      </button>

      {isOpen && hasMultipleRoles && (
        <div className="absolute bottom-full left-0 mb-1 min-w-[160px] bg-slate-800 border border-slate-700 rounded shadow-lg overflow-hidden z-50">
          {error && (
            <div className="px-3 py-1.5 text-xs text-red-400 bg-red-900/20 border-b border-red-900/30">
              {error}
            </div>
          )}
          {isFetchingRoles && (
            <div className="px-3 py-1.5 text-xs text-slate-400 flex items-center gap-2">
              <span className="w-3 h-3 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
              Laden...
            </div>
          )}
          {rolesList.length > 0 ? (
            rolesList.map(({ role }) => {
              const isActive = role === currentRole
              return (
                <button
                  key={role}
                  onClick={() => handleSwitch(role)}
                  disabled={isSwitching || isFetchingRoles}
                  className={`w-full text-left px-3 py-2 text-sm transition flex items-center gap-2 disabled:opacity-50 ${
                    isActive
                      ? 'bg-slate-700 text-white font-medium'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getRoleDotColor(role)}`} />
                  <span>{ROLE_LABELS[role]}</span>
                  {isActive && <span className="ml-auto text-green-400 text-xs font-medium">Aktiv</span>}
                </button>
              )
            })
          ) : (
            <div className="px-3 py-2 text-sm text-slate-400">Keine Rollen verfügbar</div>
          )}
        </div>
      )}
    </div>
  )
}
